import React, { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { Table, TableColumn, Select, Button, Pill } from "@foris/avocado-suite";
import { ContextApp } from "@config/Context/contextApp";
import {
  useProgrammingEditStore,
  updateSlotStage,
  toggleSlotSelection,
  toggleAllSlots,
  reunifySlot,
} from "@context/useProgrammingEditStore";
import { ProgrammingRow } from "@services/types/programming-edit/programmingEditTypes";
import AssignInstructorDrawer from "../assign-instructor-drawer";
import AssignClassroomDrawer from "../assign-classroom-drawer";
import SplitBlockDrawer from "../split-block-drawer";
import ProgrammingBulkActions from "../programming-bulk-actions";
import css from "./programmingTable.module.scss";
import { getColumnCell, getPillCell } from "@common/utils/table.util";
import ProgrammingResourceEditBtn from "../programming-resource-edit-btn";
import { blocksToHoursLabel } from "@utils/blocks";

const ProgrammingTable: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useContext(ContextApp);
  const canAssignInstructors = !!user?.abilities?.can_assign_instructors;
  const canAssignClassrooms = !!user?.abilities?.can_assign_classrooms;
  const slots = useProgrammingEditStore(state => state.slots);
  const filters = useProgrammingEditStore(state => state.filters);
  const stages = useProgrammingEditStore(state => state.stages);

  const [instructorDrawer, setInstructorDrawer] = useState<{
    slotId: string;
    currentInstructors: ProgrammingRow["instructors"];
  } | null>(null);

  const [classroomDrawer, setClassroomDrawer] = useState<{
    slotId: string;
    currentClassrooms: ProgrammingRow["classrooms"];
  } | null>(null);

  const [splitDrawer, setSplitDrawer] = useState<{
    slotId: string;
    totalBlocks: number;
  } | null>(null);

  const stageOptions = useMemo(
    () => [
      { value: "", label: t("programming-edit.table.stage-default") },
      ...stages.map(s => ({ value: s.id, label: s.name })),
    ],
    [stages],
  );

  const filteredSlots = useMemo(() => {
    return slots.filter(slot => {
      if (filters.dateFrom && slot.date < filters.dateFrom) return false;
      if (filters.dateTo && slot.date > filters.dateTo) return false;
      if (filters.dayOfWeek && slot.dayOfWeek !== filters.dayOfWeek) return false;
      if (filters.component && slot.component !== filters.component) return false;
      return true;
    });
  }, [slots, filters]);

  const formatDate = (iso: string): string => {
    if (!iso) return "";
    const [year, month, day] = iso.split("-");
    return `${day}/${month}/${year}`;
  };

  const columns: TableColumn[] = [
    {
      key: "date",
      header: t("programming-edit.table.header-date"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        return (
          <div
            className={cx(css.programmingTable_dateCell, {
              [css.programmingTable_dateCell__splitPart]: slot.splitPart,
            })}
          >
            {getColumnCell(formatDate(slot.date))}
            {slot.splitPart && (
              <Pill className={css.programmingTable_splitPill} outlined>
                {t("programming-edit.table.split-part")} {slot.splitPart}
              </Pill>
            )}
          </div>
        );
      },
      desktopOptions: { headCellStyles: { minWidth: "110px" } },
    },
    {
      key: "dayOfWeek",
      header: t("programming-edit.table.header-day"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        return getColumnCell(slot.dayOfWeek);
      },
      desktopOptions: { headCellStyles: { minWidth: "90px" } },
    },
    {
      key: "time",
      header: t("programming-edit.table.header-time"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        return getColumnCell(`${slot.startTime} - ${slot.endTime}`);
      },
      desktopOptions: { headCellStyles: { minWidth: "130px" } },
    },
    {
      key: "duration",
      header: t("programming-edit.table.header-duration"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;

        return getColumnCell(
          t("programming-edit.table.duration-value", {
            blocks: slot.durationBlocks,
            hours: blocksToHoursLabel(slot.durationBlocks),
          }),
        );
      },
      desktopOptions: { headCellStyles: { minWidth: "100px" } },
    },
    {
      key: "component",
      header: t("programming-edit.table.header-component"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        return getPillCell(slot.component);
      },
    },
    {
      key: "stage",
      header: t("programming-edit.table.header-stage"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        const currentStage =
          stageOptions.find(o => o.value === (slot.stageId ?? "")) ?? stageOptions[0];

        return (
          <div className={css.programmingTable_stageSelect}>
            <Select
              options={stageOptions}
              value={currentStage}
              onChange={opt => updateSlotStage(slot.id, (opt as any)?.value || null)}
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPlacement="auto"
              isClearable={!!currentStage.value}
            />
          </div>
        );
      },
      desktopOptions: { headCellStyles: { minWidth: "160px" } },
    },
    {
      key: "instructors",
      header: t("programming-edit.table.header-instructors"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        const adaptedResources = slot?.instructors?.map(instructor => instructor.name);

        return (
          <ProgrammingResourceEditBtn
            items={adaptedResources}
            isReadOnly={!canAssignInstructors}
            onAssign={() =>
              setInstructorDrawer({ slotId: slot.id, currentInstructors: slot.instructors })
            }
          />
        );
      },
      desktopOptions: { headCellStyles: { width: "160px" } },
    },
    {
      key: "classroom",
      header: t("programming-edit.table.header-classroom"),
      renderCell: (data: unknown) => {
        const slot = data as ProgrammingRow;
        return (
          <ProgrammingResourceEditBtn
            items={slot.classrooms.map(c => c.name)}
            isReadOnly={!canAssignClassrooms}
            onAssign={() =>
              setClassroomDrawer({ slotId: slot.id, currentClassrooms: slot.classrooms })
            }
          />
        );
      },
      desktopOptions: { headCellStyles: { width: "140px" } },
    },
  ];

  return (
    <div className={css.programmingTable}>
      <Table
        columns={columns}
        data={filteredSlots}
        rowOptions={{
          selectorType: "checkbox",
          isSelected: (data: unknown) => (data as ProgrammingRow).isSelected,
          onRowSelect: (_isSelected: boolean, data: unknown) => {
            toggleSlotSelection((data as ProgrammingRow).id);
          },
          onSelectAll: (checked: boolean) => {
            toggleAllSlots(checked);
          },
        }}
        rightActions={[
          {
            renderContent: (data: unknown) => {
              const slot = data as ProgrammingRow;

              if (slot.splitPart) {
                return (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={css.programmingTable_reunifyBtn}
                    onClick={() => slot.splitGroupId && reunifySlot(slot.splitGroupId)}
                  >
                    {t("programming-edit.table.reunify")}
                  </Button>
                );
              }

              const canSplit = slot.durationBlocks % 3 === 0 && slot.durationBlocks >= 6;

              return (
                <Button
                  variant="ghost"
                  size="sm"
                  className={css.programmingTable_splitBtn}
                  disabled={!canSplit}
                  onClick={() =>
                    setSplitDrawer({ slotId: slot.id, totalBlocks: slot.durationBlocks })
                  }
                >
                  {t("programming-edit.table.split")}
                </Button>
              );
            },
          },
        ]}
      />

      <AssignInstructorDrawer
        slotId={instructorDrawer?.slotId}
        currentInstructors={instructorDrawer?.currentInstructors}
        isOpen={!!instructorDrawer?.slotId}
        isReadOnly={!canAssignInstructors}
        onClose={() => setInstructorDrawer(null)}
      />

      {classroomDrawer && (
        <AssignClassroomDrawer
          slotId={classroomDrawer.slotId}
          currentClassrooms={classroomDrawer.currentClassrooms}
          isOpen
          isReadOnly={!canAssignClassrooms}
          onClose={() => setClassroomDrawer(null)}
        />
      )}

      <SplitBlockDrawer
        slotId={splitDrawer?.slotId}
        totalBlocks={splitDrawer?.totalBlocks}
        isOpen={!!splitDrawer?.slotId}
        onClose={() => setSplitDrawer(null)}
      />

      <ProgrammingBulkActions />
    </div>
  );
};

export default ProgrammingTable;
